import { gsap } from 'gsap';

/**
 * Module HistoryTabs
 * Gestion des onglets pour le composant "Histoire" avec animation GSAP.
 */
class HistoryTabs {
    constructor() {
        this.sections = document.querySelectorAll('.js-history-section');
        if (this.sections.length === 0) {
            return;
        }

        this.sections.forEach(section => {
            this.initSection(section);
        });
    }

    initSection(section) {
        const tabs = section.querySelectorAll('.js-history-tab');
        const contents = section.querySelectorAll('.js-history-content');
        const wrapper = section.querySelector('.history-content-wrapper');
        const indicator = section.querySelector('.history-tab-indicator');
        let isAnimating = false;

        if (tabs.length === 0 || contents.length === 0 || !wrapper || !indicator) {
            return;
        }

        this.setupInitialState(tabs, contents, wrapper, indicator);

        tabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                if (isAnimating || e.currentTarget.classList.contains('is-active')) {
                    return;
                }

                isAnimating = true;
                const targetId = e.currentTarget.dataset.target;
                const targetContent = section.querySelector(`#${targetId}`);

                if (!targetContent) {
                    isAnimating = false;
                    return;
                }

                tabs.forEach(t => t.classList.remove('is-active'));
                e.currentTarget.classList.add('is-active');
                this.moveIndicator(e.currentTarget, indicator);

                this.animate(targetContent, wrapper, () => {
                    isAnimating = false;
                });
            });
        });

        window.addEventListener('resize', () => {
            this.moveIndicator(section.querySelector('.js-history-tab.is-active'), indicator);
            this.updateContainerHeight(wrapper);
        });
    }

    updateContainerHeight(wrapper) {
        if (!wrapper) return;
        const activeContent = Array.from(wrapper.children).find(
            child => gsap.getProperty(child, "visibility") === 'visible'
        );

        if (activeContent) {
            gsap.set(wrapper, { height: activeContent.offsetHeight });
        }
    }

    setupInitialState(tabs, contents, wrapper, indicator) {
        const firstTab = tabs[0];
        firstTab.classList.add('is-active');
        this.moveIndicator(firstTab, indicator, false);

        const firstContent = contents[0];

        gsap.set(firstContent, {
            position: 'relative',
            visibility: 'visible',
            opacity: 1
        });

        const initialHeight = firstContent.offsetHeight;
        gsap.set(wrapper, { height: initialHeight });

        gsap.set(firstContent, { position: 'absolute', top: 0, left: 0, width: '100%' });

        contents.forEach((content, index) => {
            if (index !== 0) {
                gsap.set(content, {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    visibility: 'hidden',
                    opacity: 0
                });
            }
        });
    }

    moveIndicator(targetTab, indicator, animate = true) {
        if (!targetTab || !indicator) return;

        const targetRect = targetTab.getBoundingClientRect();
        const parentRect = targetTab.parentElement.getBoundingClientRect();

        const newWidth = targetRect.width;
        const newLeft = targetRect.left - parentRect.left;

        if (animate) {
            gsap.to(indicator, {
                width: newWidth,
                left: newLeft,
                duration: 0.3,
                ease: 'power3.out'
            });
        } else {
            gsap.set(indicator, {
                width: newWidth,
                left: newLeft
            });
        }
    }

    animate(newContent, wrapper, onCompleteCallback) {
        const currentContent = Array.from(wrapper.children).find(
            child => gsap.getProperty(child, "visibility") === 'visible'
        );

        gsap.set(newContent, { position: 'relative', visibility: 'hidden' });
        const newHeight = newContent.offsetHeight;
        gsap.set(newContent, { position: 'absolute' });

        const tl = gsap.timeline({ onComplete: onCompleteCallback });

        tl.to(wrapper, {
            height: newHeight,
            duration: 0.4,
            ease: 'power2.inOut'
        });

        if (currentContent) {
            tl.to(currentContent, {
                opacity: 0,
                duration: 0.3,
                ease: 'power1.in',
                onComplete: () => gsap.set(currentContent, { visibility: 'hidden' })
            }, 0);
        }

        tl.to(newContent, {
            visibility: 'visible',
            opacity: 1,
            duration: 0.3,
            ease: 'power1.out'
        }, '>-0.2');
    }
}

export function initHistoryComponent() {
    new HistoryTabs();
}
